import { useState, useEffect } from 'react';
import { Starter, BakingSession } from '../types/baking';
import { BreadAnalysis } from '../types/analysis';
import { subscribeToStarters, subscribeToBakingSessions } from './store';
import { subscribeToAnalyses } from './analysisStore';
import { logger } from './logger';
import { auth } from './firebase';

export function useStarters(userId: string | undefined) {
  const [starters, setStarters] = useState<Starter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    logger.info('useStarters effect triggered', {
      component: 'useStarters',
      userId,
      hasUserId: !!userId,
      authCurrentUser: !!auth.currentUser,
      authCurrentUserId: auth.currentUser?.uid
    });

    if (!userId) {
      logger.debug('No userId provided, clearing starters', {
        component: 'useStarters'
      });
      setStarters([]);
      setLoading(false);
      return;
    }

    try {
      logger.debug('Subscribing to starters', {
        component: 'useStarters',
        userId
      });

      const unsubscribe = subscribeToStarters(userId, (newStarters) => {
        logger.info('Starters data received', {
          component: 'useStarters',
          userId,
          count: newStarters.length
        });
        setStarters(newStarters);
        setLoading(false);
        setError(null);
      });

      return () => {
        logger.debug('Unsubscribing from starters', {
          component: 'useStarters',
          userId
        });
        unsubscribe();
      };
    } catch (err) {
      logger.error('Error in useStarters', err, {
        component: 'useStarters',
        userId
      });
      setError(err instanceof Error ? err : new Error('Failed to subscribe to starters'));
      setLoading(false);
    }
  }, [userId]);

  return { starters, loading, error };
}

export function useBakingSessions(userId: string | undefined) {
  const [bakingSessions, setBakingSessions] = useState<BakingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    logger.info('useBakingSessions effect triggered', {
      component: 'useBakingSessions',
      userId,
      hasUserId: !!userId,
      authCurrentUser: !!auth.currentUser,
      authCurrentUserId: auth.currentUser?.uid
    });

    if (!userId) {
      logger.debug('No userId provided, clearing baking sessions', {
        component: 'useBakingSessions'
      });
      setBakingSessions([]);
      setLoading(false);
      return;
    }

    try {
      logger.debug('Subscribing to baking sessions', {
        component: 'useBakingSessions',
        userId
      });

      const unsubscribe = subscribeToBakingSessions(userId, (newSessions) => {
        logger.info('Baking sessions data received', {
          component: 'useBakingSessions',
          userId,
          count: newSessions.length
        });
        setBakingSessions(newSessions);
        setLoading(false);
        setError(null);
      });

      return () => {
        logger.debug('Unsubscribing from baking sessions', {
          component: 'useBakingSessions',
          userId
        });
        unsubscribe();
      };
    } catch (err) {
      logger.error('Error in useBakingSessions', err, {
        component: 'useBakingSessions',
        userId
      });
      setError(err instanceof Error ? err : new Error('Failed to subscribe to baking sessions'));
      setLoading(false);
    }
  }, [userId]);

  return { bakingSessions, setBakingSessions, loading, error };
}

export function useAnalyses(userId: string | undefined) {
  const [analyses, setAnalyses] = useState<BreadAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    logger.info('useAnalyses effect triggered', {
      component: 'useAnalyses',
      userId,
      hasUserId: !!userId,
      authCurrentUser: !!auth.currentUser,
      authCurrentUserId: auth.currentUser?.uid
    });

    if (!userId) {
      logger.debug('No userId provided, clearing analyses', {
        component: 'useAnalyses'
      });
      setAnalyses([]);
      setLoading(false);
      return;
    }

    try {
      logger.debug('Subscribing to analyses', {
        component: 'useAnalyses',
        userId
      });

      const unsubscribe = subscribeToAnalyses(userId, (newAnalyses) => {
        logger.info('Analyses data received', {
          component: 'useAnalyses',
          userId,
          count: newAnalyses.length
        });
        setAnalyses(newAnalyses);
        setLoading(false);
        setError(null);
      });

      return () => {
        logger.debug('Unsubscribing from analyses', {
          component: 'useAnalyses',
          userId
        });
        unsubscribe();
      };
    } catch (err) {
      logger.error('Error in useAnalyses', err, {
        component: 'useAnalyses',
        userId
      });
      setError(err instanceof Error ? err : new Error('Failed to subscribe to analyses'));
      setLoading(false);
    }
  }, [userId]);

  return { analyses, loading, error };
}